/**
 * RegisterPage — multi-step enterprise onboarding for Rudrik.
 *   Step 1: Company + admin details
 *   Step 2: Email OTP verification
 *   Step 3: Success — show Tenant ID + sandbox API key + Next steps
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Lock, User, Building, Globe, Phone, FileText, MapPin,
  Briefcase, Hash, ShieldCheck, Copy, ArrowRight, CheckCircle2,
} from "lucide-react";
import { RudrikLogo } from "../components/RudrikLogo";

const API = `${window.location.origin}/api`;

const INDUSTRIES = ["Banking", "NBFC", "Fintech", "Crypto Exchange", "Insurance",
  "Wealth Management", "Stockbroker", "Payment Gateway", "Lending", "Other"];

const COMPANY_TYPES = [
  { value: "private_limited", label: "Private Limited (Pvt Ltd)" },
  { value: "public_limited",  label: "Public Limited (Ltd)" },
  { value: "llp",             label: "Limited Liability Partnership (LLP)" },
  { value: "partnership",     label: "Partnership Firm" },
  { value: "proprietorship",  label: "Sole Proprietorship" },
  { value: "trust",           label: "Trust / NGO" },
  { value: "government",      label: "Government Entity" },
];

const COUNTRIES = ["IN", "US", "GB", "AE", "SG", "AU", "CA", "DE", "FR", "Other"];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);   // 1=form, 2=otp, 3=done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // step 1 form
  const [form, setForm] = useState({
    company_name: "", company_type: "private_limited", industry: "Fintech",
    country: "IN", gst: "", website: "",
    admin_name: "", email: "", phone: "", password: "", confirm: "",
    plan: "basic",           // "basic" | "advance" (advance = Coming soon)
  });

  // step 2 OTP
  const [signupToken, setSignupToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpDebug, setOtpDebug] = useState(null);
  const [otpAttempts, setOtpAttempts] = useState(0);

  // step 3 result
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep1 = () => {
    if (!form.company_name.trim()) return "Company name is required";
    if (!form.admin_name.trim()) return "Admin full name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Valid business email required";
    if (form.password.length < 12) return "Password must be at least 12 characters";
    if (!/[A-Z]/.test(form.password)) return "Password must contain an uppercase letter";
    if (!/[0-9]/.test(form.password)) return "Password must contain a number";
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(form.password)) return "Password must contain a symbol";
    if (form.password !== form.confirm) return "Passwords do not match";
    return null;
  };

  const handleStart = async (e) => {
    e.preventDefault();
    setError("");
    const v = validateStep1();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup/start`, {
        email: form.email, password: form.password, admin_name: form.admin_name,
        company_name: form.company_name, company_type: form.company_type,
        industry: form.industry, country: form.country, gst: form.gst || null,
        website: form.website || null, phone: form.phone || null,
        plan: form.plan,
      });
      setSignupToken(data.signup_token);
      setOtpDebug(data.otp_debug);
      setStep(2);
      if (data.delivery === "console") {
        toast.warning(`Dev mode: OTP is ${data.otp_debug}`, { duration: 10000 });
      } else {
        toast.success(`Verification code sent to ${form.email}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    setError("");
    if (otp.length !== 6) { setError("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup/verify-otp`,
        { signup_token: signupToken, otp }, { withCredentials: true });
      setResult(data);
      setStep(3);
      toast.success(`Welcome to ${form.company_name}!`);
    } catch (err) {
      setOtpAttempts((a) => a + 1);
      setError(err.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/signup/resend-otp`,
        { signup_token: signupToken });
      setOtpDebug(data.otp_debug);
      if (data.delivery === "console") toast.warning(`Dev mode: OTP is ${data.otp_debug}`, { duration: 10000 });
      else toast.success("New code sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    }
  };

  const copyKey = () => {
    if (result?.tenant?.sandbox_api_key) {
      navigator.clipboard.writeText(result.tenant.sandbox_api_key);
      toast.success("API key copied — store it securely");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#080c12", display: "flex",
                  alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
      <div style={{ width: "100%", maxWidth: "480px" }}>
        <div style={{ background: "#0d1117", border: "1px solid #1e2530",
                      borderRadius: "16px", padding: "36px" }}>
          {/* Brand */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <Link to="/" data-testid="register-brand-link">
              <img
                src="/assets/rudrik-logo-full.png"
                alt="Rudrik Technology Private Limited"
                style={{ width: "100%", maxWidth: "220px", height: "auto",
                           margin: "0 auto 12px", display: "block" }}
              />
            </Link>
            <h1 data-testid="register-title" style={{ fontSize: "24px", fontWeight: 800,
              letterSpacing: "-0.6px", color: "#f1f5f9", marginTop: 4 }}>
              {step === 1 && "Start your free trial"}
              {step === 2 && "Verify your email"}
              {step === 3 && "Workspace ready 🎉"}
            </h1>
            <p style={{ color: "#94a3b8", fontSize: "13px", marginTop: 6 }}>
              {step === 1 && "Free trial · No credit card"}
              {step === 2 && `We sent a 6-digit code to ${form.email}`}
              {step === 3 && `Your AI Compliance OS is provisioned and ready`}
            </p>
          </div>

          {/* Stepper */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 28 }}>
            {[1, 2, 3].map((n) => (
              <div key={n} data-testid={`step-pill-${n}`} style={{
                height: 4, flex: 1, maxWidth: 80, borderRadius: 4,
                background: step >= n ? "#22d3ee" : "#1e2530",
                transition: "background 0.3s",
              }} />
            ))}
          </div>

          {/* STEP 1 — compact single-column signup */}
          {step === 1 && (
            <form onSubmit={handleStart} data-testid="signup-form-step1">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Select icon={Briefcase} label="Entity Type *" testId="entity-type-select"
                  value={form.company_type} onChange={(v) => set("company_type", v)}
                  options={[
                    { value: "private_limited", label: "Company" },
                    { value: "proprietorship", label: "Single Ownership (Individual)" },
                  ]} />
                <Field icon={Building}
                  label={form.company_type === "proprietorship" ? "Your Name *" : "Company Name *"}
                  testId="company-input"
                  value={form.company_name} onChange={(v) => set("company_name", v)}
                  placeholder={form.company_type === "proprietorship" ? "Rahul Sharma" : "ABC Fintech Pvt Ltd"}
                  required />
              </div>
              <Field icon={User} label="Your Full Name *" testId="name-input"
                value={form.admin_name} onChange={(v) => set("admin_name", v)}
                placeholder="Jane Smith" required />
              <div style={{ height: 12 }} />
              <Field icon={Mail} label="Email *" testId="email-input" type="email"
                value={form.email} onChange={(v) => set("email", v)}
                placeholder="you@example.com" required />
              <div style={{ height: 12 }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field icon={Lock} label="Password *" testId="password-input" type="password"
                  value={form.password} onChange={(v) => set("password", v)}
                  placeholder="Min 12 chars" required />
                <Field icon={Lock} label="Confirm *" testId="confirm-password-input" type="password"
                  value={form.confirm} onChange={(v) => set("confirm", v)}
                  placeholder="Re-enter" required />
              </div>

              <div style={{ height: 18 }} />
              <div data-testid="plan-selector"
                   style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {/* Basic — active */}
                <button type="button" onClick={() => set("plan", "basic")}
                        data-testid="plan-basic-btn"
                        style={{
                          textAlign: "left", padding: 12, borderRadius: 10, cursor: "pointer",
                          background: form.plan === "basic" ? "rgba(37,99,235,0.12)" : "#080c12",
                          border: form.plan === "basic" ? "2px solid #2563eb" : "1px solid #1e2530",
                          transition: "all 0.15s",
                        }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <ShieldCheck size={13} style={{ color: "#2563eb" }} />
                    <span style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 800 }}>Basic</span>
                    <span style={{ marginLeft: "auto", background: "rgba(34,197,94,0.15)",
                                     color: "#22c55e", fontSize: 8, fontWeight: 800,
                                     padding: "2px 5px", borderRadius: 3,
                                     textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Free
                    </span>
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 10, marginTop: 5 }}>
                    Aadhaar (DigiLocker) + PAN Verify
                  </div>
                </button>

                {/* Advance — coming soon (disabled) */}
                <button type="button" disabled
                        data-testid="plan-advance-btn"
                        style={{
                          textAlign: "left", padding: 12, borderRadius: 10,
                          background: "#080c12", border: "1px dashed #1e2530",
                          cursor: "not-allowed", opacity: 0.65,
                        }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Briefcase size={13} style={{ color: "#94a3b8" }} />
                    <span style={{ color: "#94a3b8", fontSize: 13, fontWeight: 800 }}>Advance</span>
                    <span style={{ marginLeft: "auto", background: "rgba(245,158,11,0.15)",
                                     color: "#f59e0b", fontSize: 8, fontWeight: 800,
                                     padding: "2px 5px", borderRadius: 3,
                                     textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Soon
                    </span>
                  </div>
                  <div style={{ color: "#64748b", fontSize: 10, marginTop: 5 }}>
                    Full AML, MCA, Video KYC &amp; more
                  </div>
                </button>
              </div>

              {error && <ErrorBanner text={error} />}
              <button type="submit" disabled={loading} data-testid="signup-start-btn"
                style={{ ...btnPrimary, marginTop: 16 }}>
                {loading ? "Sending code…" : <>Create account <ArrowRight size={16} /></>}
              </button>
              <div style={{ textAlign: "center", marginTop: 14, color: "#94a3b8", fontSize: 12 }}>
                Already have an account? <Link to="/login" data-testid="login-link"
                  style={{ color: "#22d3ee", fontWeight: 700, textDecoration: "none" }}>Sign in</Link>
              </div>
            </form>
          )}

          {/* STEP 2 — OTP */}
          {step === 2 && (
            <form onSubmit={handleVerify} data-testid="signup-form-step2">
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8",
                  textTransform: "uppercase", letterSpacing: 0.7, display: "block",
                  marginBottom: 10, textAlign: "center" }}>
                  Verification code
                </label>
                <input type="text" inputMode="numeric" maxLength={6} value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="otp-input"
                  style={{ width: "100%", background: "#080c12", border: "2px solid #1e2530",
                    borderRadius: 10, padding: "18px 14px", color: "#22d3ee", textAlign: "center",
                    fontSize: 32, fontWeight: 800, letterSpacing: "12px",
                    fontFamily: "ui-monospace, monospace" }}
                  placeholder="000000" autoFocus />
              </div>
              {otpDebug && (
                <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b55",
                  borderRadius: 8, padding: 12, marginBottom: 14, textAlign: "center" }}>
                  <div style={{ color: "#f59e0b", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>Dev Mode · Email not configured</div>
                  <div style={{ color: "#fbbf24", fontSize: 14, marginTop: 4 }}>
                    OTP: <span data-testid="otp-debug-value" style={{ fontFamily: "monospace", fontWeight: 800 }}>{otpDebug}</span>
                  </div>
                </div>
              )}
              {error && <ErrorBanner text={error} />}
              <button type="submit" disabled={loading || otp.length !== 6}
                data-testid="otp-verify-btn" style={btnPrimary}>
                {loading ? "Verifying…" : <>Verify & create workspace <ShieldCheck size={16} /></>}
              </button>
              <div style={{ textAlign: "center", marginTop: 16, color: "#94a3b8", fontSize: 12 }}>
                Didn't receive it? <button type="button" onClick={handleResend} data-testid="otp-resend-btn"
                  style={{ background: "none", border: "none", color: "#22d3ee", cursor: "pointer",
                  fontWeight: 700, fontSize: 12 }}>Resend code</button>
                {" · "}
                <button type="button" onClick={() => setStep(1)} data-testid="otp-back-btn"
                  style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 12 }}>
                  Change email
                </button>
              </div>
            </form>
          )}

          {/* STEP 3 — Success + Sandbox key */}
          {step === 3 && result && (
            <div data-testid="signup-success">
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%",
                  background: "rgba(34,197,94,0.15)", display: "inline-flex",
                  alignItems: "center", justifyContent: "center" }}>
                  <CheckCircle2 size={36} color="#22c55e" />
                </div>
              </div>

              <div style={{ background: "#080c12", border: "1px solid #1e2530",
                borderRadius: 10, padding: 18, marginBottom: 16 }}>
                <KV label="Tenant ID" value={result.tenant.tenant_id} testId="result-tenant-id" mono />
                <KV label="Workspace" value={result.tenant.workspace_slug} testId="result-workspace" mono />
                <KV label="Plan" value="Free Trial · 14 days" testId="result-plan" accent="#22c55e" />
                <KV label="Trial ends" value={new Date(result.tenant.trial_ends_at).toLocaleDateString()} testId="result-trial-ends" />
              </div>

              {/* Sandbox API key (shown ONCE) */}
              <div style={{ background: "rgba(34,211,238,0.06)", border: "1px solid #22d3ee44",
                borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ color: "#22d3ee", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                    Sandbox API Key
                  </span>
                  <button onClick={copyKey} data-testid="copy-api-key-btn"
                    style={{ background: "transparent", border: "1px solid #22d3ee66",
                    color: "#22d3ee", borderRadius: 6, padding: "4px 10px", fontSize: 11,
                    fontWeight: 700, cursor: "pointer", display: "inline-flex", gap: 4, alignItems: "center" }}>
                    <Copy size={11} /> Copy
                  </button>
                </div>
                <div data-testid="result-sandbox-key" style={{ color: "#f1f5f9", fontSize: 12,
                  fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }}>
                  {result.tenant.sandbox_api_key}
                </div>
                <div style={{ color: "#fbbf24", fontSize: 10, marginTop: 8 }}>
                  ⚠ Shown only once — store it in a secret manager. You can regenerate from Settings → API Keys.
                </div>
              </div>

              <button onClick={() => navigate("/dashboard")} data-testid="goto-dashboard-btn" style={btnPrimary}>
                Go to dashboard <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- shared UI helpers ----------

function SectionHeader({ text }) {
  return (
    <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>{text}</div>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 700, color: "#cbd5e1", marginBottom: 6, display: "block" }}>{label}</label>
      <div style={{ position: "relative" }}>
        <Icon size={15} style={{ position: "absolute", left: 12, top: 11, color: "#475569" }} />
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} required={required} data-testid={testId}
          style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530",
          borderRadius: 8, padding: "9px 12px 9px 36px", color: "#f1f5f9", fontSize: 13,
          fontFamily: "inherit" }} />
      </div>
    </div>
  );
}

function Select({ icon: Icon, label, value, onChange, options, testId }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 700, color: "#cbd5e1", marginBottom: 6, display: "block" }}>{label}</label>
      <div style={{ position: "relative" }}>
        <Icon size={15} style={{ position: "absolute", left: 12, top: 11, color: "#475569" }} />
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530",
          borderRadius: 8, padding: "9px 12px 9px 36px", color: "#f1f5f9", fontSize: 13,
          appearance: "none", cursor: "pointer" }}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function ErrorBanner({ text }) {
  return (
    <div data-testid="signup-error" style={{ background: "rgba(239,68,68,0.12)",
      border: "1px solid #ef444466", color: "#fca5a5", padding: "10px 14px",
      borderRadius: 8, marginTop: 14, marginBottom: 14, fontSize: 13 }}>
      {text}
    </div>
  );
}

function KV({ label, value, mono, accent, testId }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
      <span style={{ color: "#64748b", fontSize: 11, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
      <span data-testid={testId} style={{ color: accent || "#f1f5f9", fontSize: 13, fontWeight: 700,
        fontFamily: mono ? "ui-monospace, monospace" : "inherit" }}>{value}</span>
    </div>
  );
}

const btnPrimary = {
  width: "100%", background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
  color: "#fff", border: "none", borderRadius: 10, padding: "12px",
  fontSize: 14, fontWeight: 700, cursor: "pointer",
  boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
};
